const BASE = process.env.REACT_APP_API_URL || 'https://saiji-kpi-api.redmax-yokoyamamahiro.workers.dev/api';

function getToken() {
  return localStorage.getItem('kpi_token');
}

// 本部adminが「表示対象の会社」を選んだとき、その会社IDを全リクエストに付与する。
// （非本部ユーザーはサーバー側で company_id を無視するため安全）
let viewCompanyId = (typeof localStorage !== 'undefined' && localStorage.getItem('kpi_view_company')) || null;
export function setViewCompany(id) {
  viewCompanyId = id ? String(id) : null;
  if (viewCompanyId) localStorage.setItem('kpi_view_company', viewCompanyId);
  else localStorage.removeItem('kpi_view_company');
}
export function getViewCompany() { return viewCompanyId; }

async function request(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // 会社区分は廃止。company_id の付与は行わない（全社まとめて集計・表示）。
  const fullUrl = `${BASE}${path}`;

  let res;
  try {
    res = await fetch(fullUrl, { ...options, headers });
  } catch {
    // ネットワーク例外（オフライン・電波断）を現場向けの日本語に統一。
    // これ1箇所で、各画面の alert(e.message) が英語の生エラーを出さなくなる。
    throw new Error('通信できませんでした。電波の良い場所でもう一度お試しください');
  }

  // 認証切れ：トークンを破棄してログイン画面へ誘導（各画面が黙って空表示になるのを防ぐ）。
  // token があるリクエストのみ対象＝ログイン失敗(パスワード誤り)の401はここでは扱わない。
  if (res.status === 401 && token) {
    localStorage.removeItem('kpi_token');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('kpi-unauthorized'));
    }
    throw new Error('ログインの有効期限が切れました。もう一度ログインしてください');
  }

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || 'エラーが発生しました');
  }
  return data;
}

// 画面に連携先の URL（LINE の Webhook など）を出すときに使う。
export const API_BASE = BASE;

export const api = {
  login: (login_id, password) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ login_id, password }) }),
  me: () => request('/auth/me'),

  // ===== 会社（マルチテナント）=====
  getCompanies: () => request('/companies'),
  createCompany: (data) => request('/companies', { method: 'POST', body: JSON.stringify(data) }),
  updateCompany: (id, data) => request(`/companies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCompany: (id) => request(`/companies/${id}`, { method: 'DELETE' }),

  getUsers: () => request('/users'),
  // クローザー指名の選択肢（自社の有効なクローザー／両方の人・自分は除く）。ADR 0015
  getClosers: () => request('/users?closers=1'),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),

  getKpi: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/kpi${q ? '?' + q : ''}`);
  },
  createKpi: (data) => request('/kpi', { method: 'POST', body: JSON.stringify(data) }),
  updateKpi: (id, data) => request(`/kpi/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteKpi: (id) => request(`/kpi/${id}`, { method: 'DELETE' }),
  getDashboard: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/kpi/dashboard${q ? '?' + q : ''}`);
  },

  getBooths: () => request('/booths'),
  createBooth: (data) => request('/booths', { method: 'POST', body: JSON.stringify(data) }),
  updateBooth: (id, data) => request(`/booths/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteBooth: (id) => request(`/booths/${id}`, { method: 'DELETE' }),

  getBoothAnalysis: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/kpi/booth-analysis${q ? '?' + q : ''}`);
  },

  // ===== 通知設定 API =====
  registerNotifToken: (token) =>
    request('/notifications/register', { method: 'POST', body: JSON.stringify({ token }) }),
  getNotifPrefs: () => request('/notifications/prefs'),
  updateNotifPrefs: (prefs) =>
    request('/notifications/prefs', { method: 'PUT', body: JSON.stringify(prefs) }),
  deleteNotifToken: (token) =>
    request('/notifications/token', { method: 'DELETE', body: JSON.stringify({ token }) }),
  sendNotification: (type, title, body, url = '/') =>
    request('/notifications/send', { method: 'POST', body: JSON.stringify({ type, title, body, url }) }),
  sendUnsubmittedNotify: () =>
    request('/notifications/unsubmitted-notify', { method: 'POST' }),

  // ===== 新KPIシステム v2 =====

  // Shifts
  getTodayShift: () => request('/shifts/today'),
  getShifts: (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/shifts${q ? '?' + q : ''}`); },
  startShift: (data) => request('/shifts', { method: 'POST', body: JSON.stringify(data) }),
  updateShift: (id, data) => request(`/shifts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  closeShift: (id, data = {}) => request(`/shifts/${id}/close`, { method: 'POST', body: JSON.stringify(data) }),

  // Seat Memos
  getSeatMemos: (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/seat-memos${q ? '?' + q : ''}`); },
  createSeatMemo: (data) => request('/seat-memos', { method: 'POST', body: JSON.stringify(data) }),
  updateSeatMemo: (id, data) => request(`/seat-memos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSeatMemo: (id) => request(`/seat-memos/${id}`, { method: 'DELETE' }),

  // Reflections
  getReflections: (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/reflections${q ? '?' + q : ''}`); },
  getReflectionByDate: (date) => request(`/reflections/by-date?date=${date}`),
  saveReflection: (data) => request('/reflections', { method: 'POST', body: JSON.stringify(data) }),

  // 管理者：誤った日付に入力された日報の是正（編集 / 日付の付け替え / 削除）。
  // 件数(キャッチ〜獲得)は時間別入力が入力元のため、どれでも動かない。
  updateReflection: (id, data) => request(`/reflections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  moveReflection: (id, reflection_date, overwrite = false) =>
    request(`/reflections/${id}/move`, { method: 'PUT', body: JSON.stringify({ reflection_date, overwrite }) }),
  deleteReflection: (id) => request(`/reflections/${id}`, { method: 'DELETE' }),

  // フィードバック（ADR 0021: 実体は reflection_feedbacks。1日に複数人ぶん付く）。
  // saveFeedback は日報履歴からの既存導線。常に「自分名義の1件」を上書きする。
  saveFeedback: (user_id, date, feedback) => request('/reflections/feedback', { method: 'PUT', body: JSON.stringify({ user_id, date, feedback }) }),
  getMyFeedback: (limit = 5) => request(`/my-feedback?limit=${limit}`),

  // 相互フィードバック（ADR 0021）
  // getFeedbackTargets: その日一緒に入ったメンバー（3段フォールバック）＋自分が既に書いた本文
  getFeedbackTargets: (date) => request(`/feedback/targets?date=${date}`),
  savePeerFeedback: (target_user_id, date, body) =>
    request('/feedback', { method: 'PUT', body: JSON.stringify({ target_user_id, date, body }) }),
  deleteFeedback: (id) => request(`/feedback/${id}`, { method: 'DELETE' }),

  // 時間別KPI入力（1時間ごと・各自が自分の分を入力）
  getHourly: (date) => request(`/hourly?date=${date}`),
  getHourlyAll: (date) => request(`/hourly/all?date=${date}`), // 管理者：全員の時間別入力を一覧
  saveHourlyCell: (data) => request('/hourly', { method: 'PUT', body: JSON.stringify(data) }),
  saveHourlyBooth: (date, booth_id) => request('/hourly/booth', { method: 'PUT', body: JSON.stringify({ date, booth_id }) }),

  // Analytics
  getAnalyticsDashboard: (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/analytics/dashboard${q ? '?' + q : ''}`); },
  getAnalyticsFunnel: (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/analytics/funnel${q ? '?' + q : ''}`); },
  getAnalyticsBottleneck: (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/analytics/bottleneck${q ? '?' + q : ''}`); },
  getAnalyticsLostReasons: (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/analytics/lost-reasons${q ? '?' + q : ''}`); },
  getAnalyticsAverages: (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/analytics/averages${q ? '?' + q : ''}`); },
  getAnalyticsWeekly: (params = {}) => { const q = new URLSearchParams(params).toString(); return request(`/analytics/weekly${q ? '?' + q : ''}`); },

  // Enums
  getEnum: (key) => request(`/enums/${key}`),
  getAllEnums: () => request('/enums'),
  createEnum: (data) => request('/enums', { method: 'POST', body: JSON.stringify(data) }),
  updateEnum: (id, data) => request(`/enums/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEnum: (id) => request(`/enums/${id}`, { method: 'DELETE' }),

  // Thresholds (admin)
  getThresholds: () => request('/admin/thresholds'),
  updateThreshold: (id, data) => request(`/admin/thresholds/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // NSスプレッドシート連携 (admin)
  getNsSheetStatus: () => request('/admin/ns-sheet/status'),
  syncNsSheet: () => request('/admin/ns-sheet/sync', { method: 'POST' }),

  // 日報未入力の LINE 通知（本部 admin。ADR 0030）
  getLineStatus: () => request('/admin/line/status'),
  setLineGroupCompany: (groupId, companyId) => request(`/admin/line/groups/${encodeURIComponent(groupId)}`, { method: 'PUT', body: JSON.stringify({ company_id: companyId }) }),
  previewLineReminder: (date) => request(`/admin/line/preview${date ? `?date=${date}` : ''}`),
  testLineGroup: (groupId) => request('/admin/line/test', { method: 'POST', body: JSON.stringify({ group_id: groupId }) }),
};
